import { useZero } from "@rocicorp/zero/react";
import { useCallback, useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useChordShortcuts } from "../hooks/useChordShortcuts";
import { useColorMode } from "../hooks/useColorMode";
import { useCommandBar } from "../hooks/useCommandBar";
import { useKonamiCode } from "../hooks/useKonamiCode";
import { useQuery } from "../hooks/useQuery";
import { browserNotificationSettings } from "../lib/browserNotifications";
import { soundSettings } from "../lib/sounds";
import { navigate, Route, routes, Switch } from "../lib/urls";
import { Connectors } from "./Connectors";
import { Branches } from "./Branches";
import { CommandBar } from "./CommandBar";
import { CompleteProfile } from "./CompleteProfile";
import { Credits } from "./Credits";
import { Favorites } from "./Favorites";
import { GlobalModals } from "./GlobalModals";
import { NewUserIntroVideo } from "./NewUserIntroVideo";
import { PersonalChats } from "./PersonalChats";
import { Recents } from "./Recents";
import { Settings } from "./Settings";
import { Monitoring } from "./Monitoring";
import { Skills } from "./Skills";
import { Space } from "./Space";
import { ToastProvider } from "./Toast";
import { Watchtower } from "./Watchtower";
import { Workflow } from "./Workflow";

export function SignedIn() {
  const z = useZero();
  const [user] = useQuery(queries.currentUser());
  const [preferences] = useQuery(queries.userPreferences());

  useColorMode({
    bindListeners: true,
    preference:
      (preferences?.theme as "light" | "dark" | "system") ?? undefined,
    onToggle: (theme) => z.mutate(sharedMutators.setUserPreference({ theme })),
  });
  useSoundSettings(preferences);
  useBrowserNotificationSettings(preferences);
  const { open, close } = useCommandBar();
  useChordShortcuts();

  const [creditsOpen, setCreditsOpen] = useState(false);
  useKonamiCode(useCallback(() => setCreditsOpen(true), []));

  if (!user) return null;

  if (!user.name) {
    return (
      <CompleteProfile
        onSubmit={(name) =>
          z.mutate(sharedMutators.setUserName({ name: name.trim() }))
        }
      />
    );
  }

  return (
    <ToastProvider>
      {open && <CommandBar open={open} onClose={close} />}
      {creditsOpen && <Credits onClose={() => setCreditsOpen(false)} />}
      <GlobalModals />
      <NewUserIntroVideo />
      <Switch>
        <Route path={routes.personal.workflow}>
          {({ workflowId, step }) => (
            <PersonalWorkflow workflowId={workflowId} step={step} />
          )}
        </Route>
        <Route path={routes.personal}>
          <PersonalSpaceRoute />
        </Route>
        <Route path={routes.space.workflow}>
          {({ spaceId, workflowId, step }) => (
            <Workflow spaceId={spaceId} workflowId={workflowId} step={step} />
          )}
        </Route>
        <Route path={routes.space}>
          {({ spaceId }) => <Space spaceId={spaceId} />}
        </Route>
        <Route path={routes.recents}>
          <Recents />
        </Route>
        <Route path={routes.branches}>
          <Branches />
        </Route>
        <Route path={routes.favorites}>
          <Favorites />
        </Route>
        <Route path={routes.home}>
          <RedirectToHome defaultHome={preferences?.defaultHome as string} />
        </Route>
        <Route path={routes.chats}>
          <PersonalChats />
        </Route>
        <Route path={routes.connectors}>
          <Connectors />
        </Route>
        <Route path={routes.skills}>
          <Skills />
        </Route>
        <Route path={routes.monitoring}>
          <Monitoring />
        </Route>
        <Route path={routes.settings}>
          <Settings />
        </Route>
        <Route path={routes.watchtower}>
          <Watchtower />
        </Route>
        <Route path={routes.credits}>
          <Credits onClose={() => navigate(routes.recents)} />
        </Route>
        <Route>
          <RedirectToHome defaultHome={preferences?.defaultHome as string} />
        </Route>
      </Switch>
    </ToastProvider>
  );
}

function useSoundSettings(
  preferences:
    | {
        soundClicks?: string | null;
        soundChat?: string | null;
        soundWorkflow?: string | null;
      }
    | undefined
) {
  useEffect(() => {
    soundSettings.clicks = preferences?.soundClicks !== "off";
    soundSettings.chat = preferences?.soundChat !== "off";
    soundSettings.workflow = preferences?.soundWorkflow !== "off";
  }, [
    preferences?.soundClicks,
    preferences?.soundChat,
    preferences?.soundWorkflow,
  ]);
}

function useBrowserNotificationSettings(
  preferences: { browserNotifications?: string | null } | undefined
) {
  useEffect(() => {
    browserNotificationSettings.chat =
      preferences?.browserNotifications === "on";
  }, [preferences?.browserNotifications]);
}

function PersonalSpaceRoute() {
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  if (!spaceId) return null;
  return <Space spaceId={spaceId} />;
}

function PersonalWorkflow({
  workflowId,
  step,
}: {
  workflowId: string;
  step?: string;
}) {
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  if (!spaceId) return null;
  return <Workflow spaceId={spaceId} workflowId={workflowId} step={step} />;
}

function RedirectToHome({ defaultHome }: { defaultHome?: string }) {
  useEffect(() => {
    if (defaultHome === "branches") {
      navigate(routes.branches, undefined, { replace: true });
    } else if (defaultHome === "favorites") {
      navigate(routes.favorites, undefined, { replace: true });
    } else if (defaultHome === "chats") {
      navigate(routes.chats, undefined, { replace: true });
    } else if (
      defaultHome &&
      !["recents", "spaces", "chats", "monitor"].includes(defaultHome)
    ) {
      navigate(routes.space, { spaceId: defaultHome }, { replace: true });
    } else {
      navigate(routes.recents, undefined, { replace: true });
    }
  }, [defaultHome]);
  return null;
}
